import { useState, useEffect } from "react";
import { fetchWithTimeout } from "../../App";
import "../../App.css";

const API_URL = process.env.REACT_APP_API_URL;

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${process.env.REACT_APP_API_URL}${url}`;
};

function Orders() {
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState("");

  const fetchOrders = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/orders`, { credentials: "include" });
      const data = await res.json();
      if (Array.isArray(data)) setOrders(data);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to fetch orders.");
    }
  };

  const fetchMenu = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/menu`);
      const data = await res.json();
      if (Array.isArray(data)) setMenu(data);
    } catch (err) {
      console.error("Error fetching menu:", err);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/locations`);
      const data = await res.json();
      if (Array.isArray(data)) setLocations(data);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  };

  useEffect(() => {
    Promise.allSettled([fetchMenu(), fetchOrders(), fetchLocations()]);
  }, []);

  const getPrice = (itemName, storedPrice) => {
    if (storedPrice != null && parseFloat(storedPrice) > 0) return parseFloat(storedPrice);
    const found = menu.find((m) => m.name === itemName);
    return found ? parseFloat(found.price) : 0;
  };

  const getDeliveryFee = (locationName, storedFee) => {
    if (storedFee != null && storedFee !== "") return parseFloat(storedFee);
    const found = locations.find((l) => l.name === locationName);
    return found ? parseFloat(found.delivery_fee) : 0;
  };

  // ── Group flat order rows by order_token into "grouped orders" ──
  // Each grouped order has:
  //   token, name, phone, location, status, payment_status, paid,
  //   payment_proof_url, rows (all DB rows sharing the token),
  //   itemsTotal (sum of price*qty for every line),
  //   deliveryFee (counted ONCE per token),
  //   grandTotal
  const groupedOrders = Object.values(
    orders.reduce((acc, row) => {
      const token = row.order_token || `solo-${row.id}`;
      if (!acc[token]) {
        acc[token] = {
          token,
          // Use the first row's shared fields
          name: row.name,
          phone: row.phone,
          location: row.location,
          status: row.status,
          payment_status: row.payment_status,
          paid: row.paid,
          payment_proof_url: row.payment_proof_url,
          session_id: row.session_id,
          // Keep all raw rows so we can act on each id
          rows: [],
        };
      }
      acc[token].rows.push(row);
      return acc;
    }, {})
  ).map((g) => {
    const itemsTotal = g.rows.reduce(
      (sum, r) => sum + getPrice(r.item, r.item_price) * r.quantity,
      0
    );
    const deliveryFee = getDeliveryFee(g.location, g.rows[0]?.delivery_fee);
    const discountTotal = g.rows.reduce((sum, r) => sum + parseFloat(r.discount_amount || 0), 0);
    return {
      ...g,
      itemsTotal,
      deliveryFee,
      grandTotal: itemsTotal + deliveryFee - discountTotal,
      discountTotal,
      totalQty: g.rows.reduce((s, r) => s + r.quantity, 0),
    };
  });

  // Actions operate on ALL rows in the group
  const togglePaid = async (group, currentPaid) => {
    try {
      for (const row of group.rows) {
        const res = await fetch(`${API_URL}/api/admin/orders/${row.id}/paid`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ paid: !currentPaid }),
        });
        if (!res.ok) {
          const data = await res.json();
          alert(data.error || "Failed to update paid status.");
          return;
        }
      }
      fetchOrders();
    } catch (err) {
      setError("Failed to update paid status.");
    }
  };

  const updateStatus = async (group) => {
    const nextStatus =
      group.status === "Pending" ? "Ready" :
      group.status === "Ready"   ? "Completed" : "Completed";
    try {
      for (const row of group.rows) {
        const res = await fetch(`${API_URL}/api/admin/orders/${row.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ status: nextStatus }),
        });
        const data = await res.json();
        if (!res.ok) {
          alert(data.error || "Failed to update status.");
          return;
        }
      }
      fetchOrders();
    } catch (err) {
      setError("Failed to update status.");
    }
  };

  const deleteOrder = async (group) => {
    if (!window.confirm(`Delete this order (${group.rows.length} item line${group.rows.length > 1 ? "s" : ""})?`)) return;
    try {
      for (const row of group.rows) {
        await fetch(`${API_URL}/api/admin/orders/${row.id}`, {
          method: "DELETE",
          credentials: "include",
        });
      }
      fetchOrders();
    } catch (err) {
      setError("Failed to delete order.");
    }
  };

  const updatePayment = async (group, payment_status) => {
    try {
      for (const row of group.rows) {
        await fetch(`${API_URL}/api/admin/orders/${row.id}/payment`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ payment_status }),
        });
      }
      fetchOrders();
    } catch (err) {
      setError("Failed to update payment status.");
    }
  };

  const paymentStyle = (status) => {
    if (status === "submitted") return { background: "#FEF4E4", color: "var(--orange)" };
    if (status === "confirmed") return { background: "#EDF5EE", color: "var(--success)" };
    if (status === "rejected")  return { background: "#FDECEC", color: "var(--error)" };
    return { background: "var(--beige)", color: "var(--brown-mid)" };
  };

  const paymentLabel = (status) => {
    if (status === "submitted") return "⏳ Submitted";
    if (status === "confirmed") return "✅ Confirmed";
    if (status === "rejected")  return "❌ Rejected";
    return "💳 Unpaid";
  };

  const statusStyle = (status) => {
    if (status === "Pending")   return { background: "#FEF4E4", color: "var(--orange)" };
    if (status === "Ready")     return { background: "#EDF5EE", color: "var(--success)" };
    if (status === "Completed") return { background: "var(--beige)", color: "var(--text-muted)" };
    return {};
  };

  // Total revenue: sum grandTotal across grouped orders (delivery counted once each)
  const totalRevenue = groupedOrders.reduce((sum, g) => sum + g.grandTotal, 0);

  return (
    <div>
      <h1 className="admin-page-heading">📋 Orders</h1>
      <p className="admin-page-sub">Manage all incoming orders</p>

      {error && (
        <div style={{ background: "#FDECEC", border: "1px solid #E8A8A8", borderRadius: "10px", padding: "0.65rem 0.9rem", fontSize: "0.88rem", color: "var(--error)", marginBottom: "1rem" }}>
          ⚠️ {error}
        </div>
      )}

      <div className="admin-stat-card" style={{ display: "inline-block", marginBottom: "1.5rem" }}>
        <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--brown-mid)", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.04em" }}>Total Revenue</p>
        <p style={{ margin: "0.25rem 0 0", fontSize: "1.5rem", fontWeight: "bold", color: "var(--gold)", fontFamily: "'Playfair Display', Georgia, serif" }}>
          RM {totalRevenue.toFixed(2)}
        </p>
      </div>

      <div className="admin-table-wrap">
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: "var(--beige)", borderBottom: "2px solid var(--border)" }}>
              <th style={thStyle}>Token</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Phone</th>
              <th style={thStyle}>Items</th>
              <th style={thStyle}>Location</th>
              <th style={thStyle}>Price</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Payment</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupedOrders.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>
                  No orders yet.
                </td>
              </tr>
            ) : (
              groupedOrders.map((group) => (
                <tr key={group.token} style={{ borderBottom: "1px solid var(--border)" }}>

                  {/* Token */}
                  <td style={{ ...tdStyle, fontSize: "0.75rem", color: "var(--text-muted)", fontFamily: "monospace" }}>
                    {group.token}
                  </td>

                  {/* Name */}
                  <td style={tdStyle}>{group.name}</td>

                  {/* Phone */}
                  <td style={tdStyle}>
                    {group.phone?.startsWith("+") ? (
                      <a href={`https://wa.me/${group.phone.replace("+", "")}`} target="_blank" rel="noreferrer" style={{ color: "var(--orange)" }}>
                        📱 {group.phone}
                      </a>
                    ) : (
                      <span>📞 {group.phone}</span>
                    )}
                  </td>

                  {/* Items — one line per cart line */}
                  <td style={tdStyle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                      {group.rows.map((row) => (
                        <span key={row.id} style={{ fontSize: "0.88rem" }}>
                          {row.quantity}× {row.item}
                        </span>
                      ))}
                    </div>
                  </td>

                  {/* Location */}
                  <td style={tdStyle}>
                    {group.location ? (
                      group.location.startsWith("http://") || group.location.startsWith("https://") ? (
                        <a href={group.location} target="_blank" rel="noreferrer" style={{ color: "var(--orange)" }}>
                          📍 Open map →
                        </a>
                      ) : (
                        <span>📍 {group.location}</span>
                      )
                    ) : (
                      <span style={{ color: "var(--text-muted)" }}>—</span>
                    )}
                  </td>

                  {/* Price — correct: delivery counted once */}
                  <td style={tdStyle}>
                    <span style={{ fontWeight: "bold", color: "var(--gold)" }}>
                      RM {group.grandTotal.toFixed(2)}
                    </span>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.2rem" }}>
                      {group.rows.map((row) => (
                        <div key={row.id}>
                          RM {getPrice(row.item).toFixed(2)} × {row.quantity} = RM {(getPrice(row.item) * row.quantity).toFixed(2)}
                        </div>
                      ))}
                      {group.deliveryFee > 0 && (
                        <div>+ RM {group.deliveryFee.toFixed(2)} delivery</div>
                      )}
                    </div>
                  </td>

                  {/* Status */}
                  <td style={tdStyle}>
                    <span style={{
                      ...statusStyle(group.status),
                      padding: "0.25rem 0.7rem",
                      borderRadius: "999px",
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                    }}>
                      {group.status}
                    </span>
                  </td>

                  {/* Payment */}
                  <td style={tdStyle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", minWidth: "130px" }}>
                      <span style={{
                        ...paymentStyle(group.payment_status),
                        padding: "0.2rem 0.6rem",
                        borderRadius: "999px",
                        fontSize: "0.78rem",
                        fontWeight: "bold",
                        display: "inline-block",
                      }}>
                        {paymentLabel(group.payment_status)}
                      </span>

                      {group.payment_proof_url && (
                        <a
                          href={resolveUrl(group.payment_proof_url)}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: "0.78rem", color: "var(--orange)" }}
                        >
                          📎 View proof
                        </a>
                      )}

                      {group.payment_status === "submitted" && (
                        <div style={{ display: "flex", gap: "0.3rem", marginTop: "0.2rem" }}>
                          <button onClick={() => updatePayment(group, "confirmed")} className="admin-btn admin-btn--confirm">
                            ✓ Confirm
                          </button>
                          <button onClick={() => updatePayment(group, "rejected")} className="admin-btn admin-btn--reject">
                            ✕ Reject
                          </button>
                        </div>
                      )}

                      {group.payment_status === "confirmed" && (
                        <button
                          onClick={() => updatePayment(group, "unpaid")}
                          className="admin-btn admin-btn--ghost"
                        >
                          ↩ Mark unpaid
                        </button>
                      )}
                    </div>
                  </td>

                  {/* Actions */}
                  <td style={tdStyle}>
                    <div style={{ display: "flex", gap: "0.5rem", flexDirection: "column" }}>
                      {(() => {
                        const proofConfirmed = group.payment_status === "confirmed";
                        const canMarkPaid = proofConfirmed || group.paid;
                        return (
                          <div>
                            <button
                              onClick={() => canMarkPaid ? togglePaid(group, group.paid) : null}
                              disabled={!canMarkPaid}
                              title={!proofConfirmed && !group.paid ? "Confirm payment proof first" : ""}
                              className={`admin-btn ${group.paid ? "admin-btn--paid-active" : "admin-btn--paid"}`}
                            >
                              {group.paid ? "✅ Paid" : "💳 Mark Paid"}
                            </button>
                            {!proofConfirmed && !group.paid && (
                              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                                Confirm proof first
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {group.status !== "Completed" && (() => {
                        const paymentOk = group.payment_status === "confirmed";
                        const isBlocked = !paymentOk;
                        return (
                          <div>
                            <button
                              onClick={() => updateStatus(group)}
                              disabled={isBlocked}
                              title={isBlocked ? "Payment must be confirmed first" : ""}
                              className="admin-btn admin-btn--next"
                            >
                              {group.status === "Pending" ? "→ Ready" : "→ Done"}
                            </button>
                            {isBlocked && (
                              <div style={{ fontSize: "0.72rem", color: "var(--error)", marginTop: "0.25rem" }}>
                                ⚠️ Awaiting payment
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      <button onClick={() => deleteOrder(group)} className="admin-btn admin-btn--delete">
                        🗑
                      </button>
                    </div>
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thStyle = {
  padding: "0.75rem 1rem",
  textAlign: "left",
  fontSize: "0.78rem",
  fontWeight: "700",
  color: "var(--brown-mid)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const tdStyle = {
  padding: "0.75rem 1rem",
  fontSize: "0.9rem",
  color: "var(--text-main)",
  verticalAlign: "middle",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  background: "var(--warm-white)",
  overflow: "hidden",
};


export default Orders;